import { z } from 'zod';
import { validatePhoneNumber, type CountryCode } from '@/utils/phoneValidation';

/**
 * Base validation schema for designer application form (without refinements)
 * All fields marked with * are required
 */
const baseDesignerApplicationSchema = z.object({
  // Personal Information
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phoneCountryCode: z.string().min(1, 'Country code is required'),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  openToRoles: z.enum(['yes', 'maybe', 'no'], {
    required_error: 'Please select an option',
  }),
  country: z.string().min(1, 'Country is required'),
  city: z.string().min(1, 'City is required'),
  
  // Role & Experience
  portfolioLink: z.string()
    .url('Please enter a valid URL')
    .refine((url) => {
      const lowerUrl = url.toLowerCase();
      return !lowerUrl.includes('drive.google.com/file/d/') && 
             !lowerUrl.includes('drive.google.com/open?id=');
    }, 'Google Drive PDF links are not accepted. Please upload your portfolio to a public website or portfolio platform.')
    .refine((url) => {
      const lowerUrl = url.toLowerCase();
      // Check if URL ends with .pdf or contains .pdf in the path
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname.toLowerCase();
        // Check if path ends with .pdf or contains .pdf extension
        return !pathname.endsWith('.pdf') && !pathname.includes('.pdf');
      } catch {
        // If URL parsing fails, check string directly
        return !lowerUrl.endsWith('.pdf') && !lowerUrl.includes('.pdf');
      }
    }, 'PDF links are not accepted. Please provide a link to your portfolio website.')
    .refine((url) => {
      try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname.toLowerCase();
        // Reject notion.so and notion.site domains
        return !hostname.includes('notion.so') && !hostname.includes('notion.site');
      } catch {
        // If URL parsing fails, check string directly
        const lowerUrl = url.toLowerCase();
        return !lowerUrl.includes('notion.so') && !lowerUrl.includes('notion.site');
      }
    }, 'Notion page links are not accepted. Please provide a link to your portfolio website.'),
  portfolioPassword: z.enum(['yes', 'no'], {
    required_error: 'Please select an option',
  }),
  portfolioPasswordValue: z.string().optional(),
  linkedInProfile: z.string()
    .url('Please enter a valid LinkedIn URL')
    .refine((url) => {
      const lowerUrl = url.toLowerCase();
      return lowerUrl.includes('linkedin.com/in/') || lowerUrl.includes('linkedin.com/company/');
    }, 'Please enter a valid LinkedIn profile URL (e.g., linkedin.com/in/yourname)'),
  currentRoleLevel: z.enum(['junior', 'mid', 'senior', 'staff', 'principal'], {
    required_error: 'Please select your current role level',
  }),
  languages: z.array(
    z.object({
      languageId: z.string().min(1, 'Language ID is required'),
      proficiency: z.enum(['beginner', 'intermediate', 'advanced', 'native'], {
        required_error: 'Please select a proficiency level',
      }),
    })
  ).min(1, 'Please select at least one language'),
  
  // Work Preferences
  nextRoleLevel: z.enum(['mid', 'senior', 'staff', 'principal'], {
    required_error: 'Please select your next role level',
  }),
  workSetup: z.array(z.enum(['remote', 'hybrid', 'in-office'])).min(1, 'Please select at least one work setup option'),
  workEnvironment: z.array(z.enum([
    'solo',
    'small-teams',
    'medium-teams',
    'large-teams',
    'startups',
    'big-companies'
  ])).min(1, 'Please select at least one work environment'),
  expectedSalaryRange: z.enum([
    // Europe ranges
    '35k-50k', '50k-70k', '70k-100k', '100k-150k', '150k-plus',
    // Non-European ranges
    '60k-90k', '90k-130k', '150k-200k', '200k-plus',
    // Africa ranges
    '15k-25k', '25k-45k', '45k-70k', '70k-110k', '110k-plus',
    // Latin America ranges
    '18k-30k', '30k-50k', '50k-80k', '80k-120k', '120k-plus',
    // Asia ranges
    '12k-25k', '20k-40k', '40k-65k', '65k-100k', '100k-plus'
  ], {
    required_error: 'Please select your expected salary range',
  }),
  
  // Availability
  readyToBegin: z.enum(['immediately', '7-days', '14-days', '30-days', 'other'], {
    required_error: 'Please select when you can begin',
  }),
  openToFutureContact: z.enum(['yes', 'no'], {
    required_error: 'Please select an option',
  }),
  
  // Legal Agreements
  acceptedTermsOfService: z.boolean().refine(val => val === true, 'You must accept the Terms of Service'),
  acceptedPrivacyPolicy: z.boolean().refine(val => val === true, 'You must accept the Privacy Policy'),
  acceptedCookiePolicy: z.boolean().refine(val => val === true, 'You must accept the Cookie Policy'),
});

/**
 * Client-side validation schema with refinements
 */
export const designerApplicationSchema = baseDesignerApplicationSchema
  .refine((data) => {
    if (data.portfolioPassword === 'yes' && (!data.portfolioPasswordValue || data.portfolioPasswordValue.trim() === '')) {
      return false;
    }
    return true;
  }, {
    message: 'Portfolio password is required when portfolio is password protected',
    path: ['portfolioPasswordValue'],
  })
  .refine((data) => {
    if (!data.phoneCountryCode || !data.phoneNumber) {
      return true; // Let other validations handle required fields
    }
    const validation = validatePhoneNumber(data.phoneNumber, data.phoneCountryCode as CountryCode);
    return validation.isValid;
  }, (data) => {
    if (!data.phoneCountryCode || !data.phoneNumber) {
      return { message: 'Phone number is required', path: ['phoneNumber'] };
    }
    const validation = validatePhoneNumber(data.phoneNumber, data.phoneCountryCode as CountryCode);
    return {
      message: validation.error || 'Invalid phone number for selected country',
      path: ['phoneNumber'],
    };
  });

/**
 * TypeScript type inferred from the Zod schema
 */
export type DesignerApplicationFormData = z.infer<typeof designerApplicationSchema>;

/**
 * Server-side validation schema including Turnstile token
 * Extends the base schema with optional bot protection (disabled in development)
 */
export const designerApplicationSchemaWithTurnstile = baseDesignerApplicationSchema
  .extend({
    turnstileToken: z.string().optional(),
    // Apply-link code from /apply/<code> — optional, ties the submission to a job
    applyCode: z.string().regex(/^[A-Za-z0-9]{10,16}$/).optional(),
    // Published JD version the applicant saw — verified server-side against the link
    jdVersionId: z.string().uuid().optional(),
  })
  .refine((data) => {
    if (data.portfolioPassword === 'yes' && (!data.portfolioPasswordValue || data.portfolioPasswordValue.trim() === '')) {
      return false;
    }
    return true;
  }, {
    message: 'Portfolio password is required when portfolio is password protected',
    path: ['portfolioPasswordValue'],
  })
  .refine((data) => {
    if (!data.phoneCountryCode || !data.phoneNumber) {
      return true; // Let other validations handle required fields
    }
    const validation = validatePhoneNumber(data.phoneNumber, data.phoneCountryCode as CountryCode);
    return validation.isValid;
  }, (data) => {
    if (!data.phoneCountryCode || !data.phoneNumber) {
      return { message: 'Phone number is required', path: ['phoneNumber'] };
    }
    const validation = validatePhoneNumber(data.phoneNumber, data.phoneCountryCode as CountryCode);
    return {
      message: validation.error || 'Invalid phone number for selected country',
      path: ['phoneNumber'],
    };
  });

